import { z } from "zod";

import { zBool, zId, zNumber, zText, zTimestamp } from "@/shared/api/schema";

/* Leads, cold calls and farming are three sources of the same thing: a person who may buy or
   sell. The API shapes differ, so each is read by its own schema and mapped to one ClientRecord
   that every list, sheet and profile uses. */

export type ClientKind = "lead" | "cold-call" | "farming";

const personRef = z.looseObject({
  id: zId,
  firstname: zText,
  lastname: zText,
  picture_url: zText.optional(),
});

/** assigned_to arrives as an id or, when the relation is loaded under the same name, a user. */
const idOrUser = z
  .union([zId, z.looseObject({ id: zId })])
  .nullish()
  .transform((value) =>
    value === null || value === undefined ? null : typeof value === "number" ? value : value.id,
  );

export interface ClientPerson {
  id: number;
  name: string;
  picture: string | null;
}

export interface ClientUnit {
  id: number;
  code: string;
  type: string | null;
  phase: string | null;
  developerId: number | null;
  projectId: number | null;
}

/** A lead's place in the journey: its status timer, its previous owner, its reservation. */
export interface LeadJourney {
  /** When the time on its status runs out (working time); null = no limit or paused. */
  deadlineAt: string | null;
  statusChangedAt: string | null;
  rotationCount: number;
  /** It went round the whole team and waits with the team leader, in the Cold calls pool. */
  rotationPaused: boolean;
  recycledCount: number;
  developer: { id: number; name: string; image: string | null } | null;
  project: { id: number; name: string } | null;
  unitNumber: string | null;
  reservedAt: string | null;
  closedAt: string | null;
  /** What the closed deal was worth, in EGP. */
  dealValue: number | null;
  commentsCount: number;
  documentsCount: number;
  meetingsCount: number;
  latestComment: { body: string; author: string; createdAt: string | null } | null;
}

export interface ClientRecord {
  kind: ClientKind;
  id: number;
  name: string;
  phone: string | null;
  secondPhone: string | null;
  email: string | null;
  address: string | null;
  notes: string | null;
  sourceId: number | null;
  /** Leads only: the campaign they came from. */
  campaignId: number | null;
  /** Leads only: the areas they want to buy in. */
  areaIds: number[];
  statusId: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  assignedToId: number | null;
  assignedTo: ClientPerson | null;
  assignedBy: ClientPerson | null;
  interestBuy: boolean;
  interestSell: boolean;
  /** Farming only: the owner's unit. */
  unit: ClientUnit | null;
  /** Leads only. */
  journey: LeadJourney | null;
}

function person(ref: z.infer<typeof personRef> | null | undefined): ClientPerson | null {
  if (!ref) return null;
  const name = [ref.firstname, ref.lastname].filter(Boolean).join(" ");
  return { id: ref.id, name, picture: ref.picture_url || null };
}

const base = {
  id: zId,
  name: z.string(),
  phone: zText,
  second_phone: zText,
  notes: zText,
  current_status_id: zId.nullish(),
  created_at: zTimestamp,
  updated_at: zTimestamp,
  assigned_to: idOrUser,
};

const leadSchema = z.looseObject({
  ...base,
  email: zText,
  address: zText.optional(),
  source_id: zId.nullish(),
  campaign_id: zId.nullish(),
  status_changed_at: zTimestamp.optional(),
  unit_number: zText.optional(),
  reserved_at: zTimestamp.optional(),
  closed_at: zTimestamp.optional(),
  deal_value: zNumber.optional(),
  rotation_count: zNumber.optional(),
  rotation_paused: zBool.optional(),
  recycled_count: zNumber.optional(),
});

const journeySchema = z.looseObject({
  deadline_at: zTimestamp,
  developer: z.looseObject({ id: zId, name: z.string(), image: zText }).nullish(),
  project: z.looseObject({ id: zId, name: z.string() }).nullish(),
  area_ids: z.array(zId).optional(),
  comments_count: zNumber,
  documents_count: zNumber,
  meetings_count: zNumber,
  latest_comment: z
    .looseObject({ body: z.string(), author: zText, created_at: zTimestamp })
    .nullish(),
});
const coldCallSchema = z.looseObject({
  ...base,
  email: zText,
  address: zText,
  source_id: zId.nullish(),
  interest_buy: zBool,
  interest_sell: zBool,
});
const unitRefSchema = z.looseObject({
  id: zId,
  unit_code: zText,
  unit_type: zText,
  phase: zText,
  developer_id: zId.nullish(),
  project_id: zId.nullish(),
});
const farmingSchema = z.looseObject({
  ...base,
  inventory_id: zId.nullish(),
  interested_to_buy: zBool,
  interested_to_sell: zBool,
  inventory: unitRefSchema.nullish(),
});

const wrapper = { assigned_to_user: personRef.nullish(), assigned_by_user: personRef.nullish() };

function common(
  kind: ClientKind,
  raw: z.infer<typeof leadSchema> | z.infer<typeof coldCallSchema> | z.infer<typeof farmingSchema>,
  people: z.infer<z.ZodObject<typeof wrapper>>,
) {
  return {
    kind,
    id: raw.id,
    name: raw.name,
    phone: raw.phone,
    secondPhone: raw.second_phone,
    notes: raw.notes,
    statusId: raw.current_status_id ?? null,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
    assignedToId: raw.assigned_to,
    assignedTo: person(people.assigned_to_user),
    assignedBy: person(people.assigned_by_user),
  };
}

export const leadItemSchema = z
  .looseObject({ lead: leadSchema, ...wrapper, journey: journeySchema.optional() })
  .transform((item): ClientRecord => {
    const { lead, journey } = item;
    const comment = journey?.latest_comment;
    return {
      ...common("lead", lead, item),
      email: lead.email,
      address: lead.address ?? null,
      sourceId: lead.source_id ?? null,
      campaignId: lead.campaign_id ?? null,
      areaIds: journey?.area_ids ?? [],
      interestBuy: false,
      interestSell: false,
      unit: null,
      journey: {
        deadlineAt: journey?.deadline_at ?? null,
        statusChangedAt: lead.status_changed_at ?? null,
        rotationCount: lead.rotation_count ?? 0,
        rotationPaused: lead.rotation_paused ?? false,
        recycledCount: lead.recycled_count ?? 0,
        developer: journey?.developer ?? null,
        project: journey?.project ?? null,
        unitNumber: lead.unit_number ?? null,
        reservedAt: lead.reserved_at ?? null,
        closedAt: lead.closed_at ?? null,
        dealValue: lead.deal_value ?? null,
        commentsCount: journey?.comments_count ?? 0,
        documentsCount: journey?.documents_count ?? 0,
        meetingsCount: journey?.meetings_count ?? 0,
        latestComment: comment
          ? { body: comment.body, author: comment.author ?? "", createdAt: comment.created_at }
          : null,
      },
    };
  });

export const coldCallItemSchema = z
  .looseObject({ cold_call: coldCallSchema, ...wrapper })
  .transform((item): ClientRecord => ({
    ...common("cold-call", item.cold_call, item),
    email: item.cold_call.email,
    address: item.cold_call.address,
    sourceId: item.cold_call.source_id ?? null,
    campaignId: null,
    areaIds: [],
    interestBuy: item.cold_call.interest_buy,
    interestSell: item.cold_call.interest_sell,
    unit: null,
    journey: null,
  }));

export const farmingItemSchema = z
  .looseObject({ farming: farmingSchema, ...wrapper })
  .transform((item): ClientRecord => ({
    ...common("farming", item.farming, item),
    email: null,
    address: null,
    sourceId: null,
    campaignId: null,
    areaIds: [],
    interestBuy: item.farming.interested_to_buy,
    interestSell: item.farming.interested_to_sell,
    unit: item.farming.inventory
      ? {
          id: item.farming.inventory.id,
          code: item.farming.inventory.unit_code ?? "",
          type: item.farming.inventory.unit_type,
          phase: item.farming.inventory.phase,
          developerId: item.farming.inventory.developer_id ?? null,
          projectId: item.farming.inventory.project_id ?? null,
        }
      : null,
    journey: null,
  }));

/** One status change, from any of the three history shapes. */
export interface StatusChange {
  statusId: number | null;
  statusName: string;
  /** Null when the system made the change (a rotation). */
  by: string | null;
  at: string | null;
  notes?: string | null;
}

export const leadHistorySchema = z
  .looseObject({
    status_id: zId.nullish(),
    status_name: zText,
    agent_name: zText,
    notes: zText.optional(),
    changed_at: zTimestamp,
  })
  .transform((row): StatusChange => ({
    statusId: row.status_id ?? null,
    statusName: row.status_name ?? "",
    by: row.agent_name,
    at: row.changed_at,
    notes: row.notes ?? null,
  }));

export const coldCallHistorySchema = z
  .looseObject({
    status: z.looseObject({ id: zId, name: z.string() }),
    changed_by: z.looseObject({ name: zText }).nullish(),
    changed_at: zTimestamp,
  })
  .transform((row): StatusChange => ({
    statusId: row.status.id,
    statusName: row.status.name,
    by: row.changed_by?.name ?? null,
    at: row.changed_at,
  }));

export const farmingHistorySchema = z
  .looseObject({
    status_id: zId,
    created_at: zTimestamp,
    status: z.looseObject({ name: z.string() }).nullish(),
    changed_by: z.looseObject({ firstname: zText, lastname: zText }).nullish(),
  })
  .transform((row): StatusChange => ({
    statusId: row.status_id,
    statusName: row.status?.name ?? "",
    by: row.changed_by
      ? [row.changed_by.firstname, row.changed_by.lastname].filter(Boolean).join(" ")
      : null,
    at: row.created_at,
  }));
